import React, { useState, useRef, useEffect } from 'react'
import { useNotification } from '../contexts/NotificationContext'
import { apiPost } from '../lib/api'

interface EnrollmentModalProps {
  isOpen: boolean
  onClose: () => void
}

interface MultiSelectProps {
  options: string[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder: string
}

function MultiSelect({ options, selected, onChange, placeholder }: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option))
    } else {
      onChange([...selected, option])
    }
  }

  const removeOption = (option: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(selected.filter(s => s !== option))
  }

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm cursor-pointer bg-white min-h-[38px] flex flex-wrap gap-1 items-center"
      >
        {selected.length === 0 ? (
          <span className="text-gray-400">{placeholder}</span>
        ) : (
          selected.map(item => (
            <span
              key={item}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded"
            >
              {item.length > 20 ? item.substring(0, 20) + '...' : item}
              <button
                type="button"
                onClick={(e) => removeOption(item, e)}
                className="hover:text-blue-600"
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {options.map(option => (
            <div
              key={option}
              onClick={() => toggleOption(option)}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                selected.includes(option) ? 'bg-blue-50' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => {}}
                className="h-4 w-4 text-blue-600 rounded"
              />
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function EnrollmentModal({ isOpen, onClose }: EnrollmentModalProps) {
  const { showSuccess, showError } = useNotification()
  const [formData, setFormData] = useState({
    organizationName: '',
    contactName: '',
    email: '',
    phone: '',
    location: '',
    facilityType: '',
    medicalSpecialties: [] as string[],
    currentEmr: '',
    preferredLanguages: [] as string[],
    branches: 1,
    staffStrength: 2,
    deploymentOption: '',
    integrations: [] as string[],
    additionalInfo: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => {
      const updated = { ...prev, [name]: value }
      if (name === 'location') {
        updated.integrations = []
      }
      return updated
    })
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const numValue = Math.max(1, parseInt(value) || 1)
    setFormData(prev => ({ ...prev, [name]: numValue }))
  }

  const medicalSpecialtiesList = [
    'General Practice', 'Internal Medicine', 'Pediatrics', 'OB/GYN', 'Surgery',
    'Orthopedics', 'Cardiology', 'Dermatology', 'Psychiatry', 'Emergency',
    'Oncology', 'Neurology', 'Ophthalmology', 'ENT', 'Radiology',
    'Laboratory', 'Pharmacy', 'Dental', 'Physiotherapy', 'Other',
  ]

  const languageOptions = [
    'English', 'French', 'Spanish', 'Portuguese', 'Yoruba',
    'Igbo', 'Hausa', 'Pidgin', 'Twi', 'Wolof', 'Other',
  ]

  const currentEmrOptions = [
    'None (New)', 'Paper-based', 'Epic', 'Cerner', 'Allscripts',
    'eClinicalWorks', 'OSCAR', 'Accuro', 'Telus Health', 'Other',
  ]

  const getLocationIntegrations = () => {
    const common = [
      'HL7 Integration',
      'Lab Systems',
      'Pharmacy Systems',
      'Imaging Systems',
      'Telemedicine Platforms',
    ]

    switch (formData.location) {
      case 'canada':
        return [
          ...common,
          'OHIP Billing',
          'MSP Billing (BC)',
          'RAMQ Billing (QC)',
          'AHCIP Billing (AB)',
          'SADIE Forms',
          'Health Card Validation',
          'Canadian Pharmacy Network',
          'Canada Health Infoway',
          'Provincial Health Networks',
          'E-Prescribing (Canada)',
          'Narcotics Monitoring',
        ]

      case 'north-america':
        return [
          ...common,
          'HL7 FHIR',
          'Epic Integration',
          'Cerner Integration',
          'Allscripts Integration',
          'eClinicalWorks Integration',
          'Medicare/Medicaid',
          'Commercial Insurance',
          'US Pharmacy Networks',
          'E-Prescribing (Surescripts)',
          'HIE Integration',
          'Meaningful Use Compliance',
        ]

      case 'nigeria':
        return [
          ...common,
          'NHIS Integration',
          'State Health Insurance',
          'Nigerian Pharmacy Board',
          'Nigerian Medical Association',
          'Mobile Money (Nigeria)',
          'Bank Transfer Integration',
          'POS Integration',
          'Government Health Systems',
          'Primary Healthcare Networks',
          'Referral Systems',
          'Community Health Programs',
        ]

      case 'west-africa':
        return [
          ...common,
          'Mobile Money (MTN, Orange, etc.)',
          'Bank Transfer Integration',
          'POS Integration',
          'Government Health Systems',
          'Regional Health Networks',
          'Community Health Programs',
          'Referral Systems',
          'Pharmacy Networks (West Africa)',
          'Laboratory Networks',
          'Insurance Providers (Regional)',
        ]

      default:
        return [
          ...common,
          'Billing Systems',
          'Insurance Providers',
          'Payment Gateways',
          'Government Health Systems',
        ]
    }
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.organizationName.trim()) {
      newErrors.organizationName = 'Organization name is required'
    }
    if (!formData.contactName.trim()) {
      newErrors.contactName = 'Contact name is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    }
    if (!formData.location) {
      newErrors.location = 'Location is required'
    }
    if (!formData.facilityType) {
      newErrors.facilityType = 'Facility type is required'
    }
    if (formData.staffStrength < 2) {
      newErrors.staffStrength = 'Staff strength must be at least 2'
    }
    if (formData.branches < 1) {
      newErrors.branches = 'Number of locations must be at least 1'
    }
    if (!formData.deploymentOption) {
      newErrors.deploymentOption = 'Deployment option is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setIsSubmitting(true)

    try {
      const response = await apiPost('/enrollment', {
        organizationName: formData.organizationName,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        facilityType: formData.facilityType,
        medicalSpecialties: formData.medicalSpecialties,
        currentEmr: formData.currentEmr || undefined,
        preferredLanguages: formData.preferredLanguages,
        branches: formData.branches,
        staffStrength: formData.staffStrength,
        deploymentOption: formData.deploymentOption,
        integrations: formData.integrations,
        additionalInfo: formData.additionalInfo || undefined,
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error?.message || data.message || 'Failed to submit enrollment request')
      }

      showSuccess('Thank you! We\'ll contact you shortly to complete your enrollment.')
      onClose()
      setFormData({
        organizationName: '',
        contactName: '',
        email: '',
        phone: '',
        location: '',
        facilityType: '',
        medicalSpecialties: [],
        currentEmr: '',
        preferredLanguages: [],
        branches: 1,
        staffStrength: 2,
        deploymentOption: '',
        integrations: [],
        additionalInfo: '',
      })
    } catch (error) {
      console.error('Enrollment error:', error)
      showError(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-blue-600 px-4 py-5 sm:px-6 sm:py-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl sm:text-2xl font-bold text-white" id="modal-title">
                Get Started with EMR
              </h3>
              <button
                onClick={onClose}
                className="rounded-md text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white p-2"
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="mt-2 text-sm sm:text-base text-blue-100">
              Fill out the form below and we'll get in touch to set up your EMR system.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-4 py-4 sm:px-6">
            <div className="space-y-4">
              {/* Organization Name */}
              <div>
                <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 sm:py-2.5 border rounded-md text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.organizationName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your organization name"
                />
                {errors.organizationName && (
                  <p className="mt-1 text-sm text-red-600">{errors.organizationName}</p>
                )}
              </div>

              {/* Contact Name and Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 sm:py-2.5 border rounded-md text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.contactName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Your full name"
                  />
                  {errors.contactName && (
                    <p className="mt-1 text-sm text-red-600">{errors.contactName}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 sm:py-2.5 border rounded-md text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Phone and Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 sm:py-2.5 border rounded-md text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="+1 (555) 123-4567"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 sm:py-2.5 border rounded-md text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.location ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select location</option>
                    <option value="canada">Canada</option>
                    <option value="north-america">North America (US)</option>
                    <option value="nigeria">Nigeria</option>
                    <option value="west-africa">West Africa</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                  )}
                </div>
              </div>

              {/* Facility Type, Staff, Locations - 3 columns */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label htmlFor="facilityType" className="block text-sm font-medium text-gray-700 mb-1">
                    Facility Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="facilityType"
                    name="facilityType"
                    value={formData.facilityType}
                    onChange={handleChange}
                    className={`w-full px-2 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.facilityType ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select</option>
                    <option value="hospital">Hospital</option>
                    <option value="clinic">Clinic</option>
                    <option value="private-practice">Private Practice</option>
                    <option value="community-health">Community Health</option>
                    <option value="urgent-care">Urgent Care</option>
                    <option value="specialty-center">Specialty Center</option>
                    <option value="pharmacy">Pharmacy</option>
                    <option value="laboratory">Laboratory</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.facilityType && <p className="mt-1 text-xs text-red-600">{errors.facilityType}</p>}
                </div>
                <div>
                  <label htmlFor="staffStrength" className="block text-sm font-medium text-gray-700 mb-1">
                    Staff <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="staffStrength"
                    name="staffStrength"
                    min="2"
                    value={formData.staffStrength}
                    onChange={handleNumberChange}
                    className={`w-full px-2 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.staffStrength ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.staffStrength && <p className="mt-1 text-xs text-red-600">{errors.staffStrength}</p>}
                </div>
                <div>
                  <label htmlFor="branches" className="block text-sm font-medium text-gray-700 mb-1">
                    Locations <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="branches"
                    name="branches"
                    min="1"
                    value={formData.branches}
                    onChange={handleNumberChange}
                    className={`w-full px-2 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.branches ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.branches && <p className="mt-1 text-xs text-red-600">{errors.branches}</p>}
                </div>
              </div>

              {/* Medical Specialties and Languages - Multi-select dropdowns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medical Specialties
                  </label>
                  <MultiSelect
                    options={medicalSpecialtiesList}
                    selected={formData.medicalSpecialties}
                    onChange={(selected) => setFormData(prev => ({ ...prev, medicalSpecialties: selected }))}
                    placeholder="Select specialties..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Languages
                  </label>
                  <MultiSelect
                    options={languageOptions}
                    selected={formData.preferredLanguages}
                    onChange={(selected) => setFormData(prev => ({ ...prev, preferredLanguages: selected }))}
                    placeholder="Select languages..."
                  />
                </div>
              </div>

              {/* Current EMR and Deployment - 2 columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="currentEmr" className="block text-sm font-medium text-gray-700 mb-1">
                    Current EMR System
                  </label>
                  <select
                    id="currentEmr"
                    name="currentEmr"
                    value={formData.currentEmr}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select current system</option>
                    {currentEmrOptions.map((emr) => (
                      <option key={emr} value={emr}>{emr}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="deploymentOption" className="block text-sm font-medium text-gray-700 mb-1">
                    Deployment <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="deploymentOption"
                    name="deploymentOption"
                    value={formData.deploymentOption}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.deploymentOption ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select option</option>
                    <option value="cloud">Cloud EMR</option>
                    <option value="offline-first">Offline-First</option>
                    <option value="portable">Portable EMR</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="not-sure">Not sure</option>
                  </select>
                  {errors.deploymentOption && <p className="mt-1 text-xs text-red-600">{errors.deploymentOption}</p>}
                </div>
              </div>

              {/* Integrations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Required Integrations
                  {formData.location && (
                    <span className="ml-1 text-xs text-gray-500 font-normal">
                      ({formData.location === 'canada' ? 'Canada' :
                        formData.location === 'north-america' ? 'US' :
                        formData.location === 'nigeria' ? 'Nigeria' :
                        formData.location === 'west-africa' ? 'W. Africa' : 'Common'})
                    </span>
                  )}
                </label>
                {!formData.location ? (
                  <p className="text-sm text-gray-400 italic">Select a location first</p>
                ) : (
                  <MultiSelect
                    options={getLocationIntegrations()}
                    selected={formData.integrations}
                    onChange={(selected) => setFormData(prev => ({ ...prev, integrations: selected }))}
                    placeholder="Select integrations..."
                  />
                )}
              </div>

              {/* Additional Information */}
              <div>
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Information
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Specific requirements, timelines, or questions..."
                />
              </div>
            </div>

            {/* Footer Actions */}
            <div className="mt-6 sm:mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-6 py-3 border border-gray-300 rounded-md text-base sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition touch-manipulation"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto px-6 py-3 border border-transparent rounded-md text-base sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition touch-manipulation min-h-[44px] flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Enrollment Request'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

